import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, MenuItem, Select, InputLabel, FormControl, Box, List, ListItem, ListItemText, Tooltip, IconButton, Typography } from '@mui/material';
import UserSelectionModal from '../../components/UserSelectionModal';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { useAdmin } from '../../contexts/AdminContext';

const defaultForm = {
  userId: '',
  amount: '',
  currency: 'EUR',
  startDate: '',
  endDate: '',
  status: 'active',
  paymentType: 'cash',
};

const ModalMember = ({ open, onClose, mode, member, onSuccess, users = [], membershipList = [] }) => {
  const { addMember, updateMember, error, clearError } = useAdmin();
  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const isAdd = mode === 'add';

  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [membershipModalOpen, setMembershipModalOpen] = useState(false);
  const [memberUser, setMemberUser] = useState(null);


  useEffect(() => {
    if (isEdit || isView) {
      setForm({
        userId: member?.userId,
        amount: member?.amount,
        currency: member?.currency,
        startDate: member?.startDate ? member.startDate.slice(0, 10) : '',
        endDate: member?.endDate ? member.endDate.slice(0, 10) : '',
        status: member?.status,
        paymentType: member?.paymentType,
      });
      setSelectedUser(null);
      setSelectedMembership(null);

      // Find user data from users array if available
      if (member?.userId && users.length > 0) {
        const user = users.find((u) => u._id === member.userId);
        setMemberUser(user || null);
      } else {
        setMemberUser(null);
      }
    } else {
      setForm(defaultForm);
      setSelectedUser(null);
      setSelectedMembership(null);
      setMemberUser(null);
    }
    clearError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, member, isEdit, isView, users]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setForm((prev) => ({ ...prev, userId: user._id }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    clearError();
    try {
      if (isAdd) {
        // Invia userId, membershipId e paymentType
        const result = await addMember({
          userId: selectedUser._id,
          membershipListId: selectedMembership._id,
          paymentType: form.paymentType,
        });
        
        if (result.success) {
          onSuccess && onSuccess(result.data);
        }
      } else if (isEdit && member?._id) {
        console.log('form', form);
        console.log('member', member);
        const result = await updateMember({
          _id: member._id,
          ...form,
        });
        if (result.success) {
          onSuccess && onSuccess(result.data);
        }
      }
      // onClose();
    } catch (e) {
      // Error will be handled by the admin context
      console.error('Error in handleSubmit:', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }}
      maxWidth='sm'
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle>
        {isAdd && 'Aggiungi Tesserato'}
        {isEdit && 'Modifica Tesserato'}
        {isView && 'Dettagli Tesserato'}
      </DialogTitle>
      <DialogContent>
        <Box component='form' sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {isAdd ? (
            <>
              {/* Selezione utente */}
              {!selectedUser && (
                <Button variant='outlined' onClick={() => setUserModalOpen(true)} sx={{ mb: 1 }}>
                  Seleziona Utente
                </Button>
              )}
              {selectedUser && (
                <Box sx={{ p: 2, mb: 2, borderColor: 'grey.300', borderWidth: 1, borderStyle: 'solid', borderRadius: 2, display: 'flex', alignItems: 'center', width: '100%', minHeight: 56 }}>
                  <Typography variant='body1' fontWeight='bold' noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', flexGrow: 1 }}>
                    {selectedUser.name} {selectedUser.surname}
                  </Typography>
                  <Typography variant='body2' color='text.secondary' sx={{ ml: 2 }}>
                    {selectedUser.email}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                    <Tooltip title='Deseleziona utente'>
                      <IconButton
                        aria-label='Deseleziona utente'
                        color='error'
                        onClick={() => {
                          setSelectedUser(null);
                          setForm((prev) => ({ ...prev, userId: '' }));
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Cambia utente'>
                      <IconButton aria-label='Cambia utente' color='primary' onClick={() => setUserModalOpen(true)}>
                        <PersonSearchIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )}
              <UserSelectionModal open={userModalOpen} onClose={() => setUserModalOpen(false)} onSelect={handleUserSelect} users={users} />
              {/* Selezione tesseramento */}
              {!selectedMembership && (
                <Button variant='outlined' onClick={() => setMembershipModalOpen(true)} sx={{ mb: 1 }}>
                  Seleziona Tesseramento
                </Button>
              )}
              {selectedMembership && (
                <Box sx={{ p: 2, mb: 2, borderColor: 'grey.300', borderWidth: 1, borderStyle: 'solid', borderRadius: 2, display: 'flex', alignItems: 'center', width: '100%', minHeight: 56 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant='body1' fontWeight='bold' noWrap>
                      {selectedMembership.name || selectedMembership.title || 'Tesseramento'}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {selectedMembership.description}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {selectedMembership.startDate && `Inizio: ${dayjs(selectedMembership.startDate).locale('it').format('DD-MMM-YY')}`}
                      {selectedMembership.endDate && `  Fine: ${dayjs(selectedMembership.endDate).locale('it').format('DD-MMM-YY')}`}
                      {typeof selectedMembership.amount !== 'undefined' && `  Importo: €${selectedMembership.amount}`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                    <Tooltip title='Deseleziona tesseramento'>
                      <IconButton aria-label='Deseleziona tesseramento' color='error' onClick={() => setSelectedMembership(null)}>
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Cambia tesseramento'>
                      <IconButton aria-label='Cambia tesseramento' color='primary' onClick={() => setMembershipModalOpen(true)}>
                        <PersonSearchIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )}
              {/* Modal di selezione tesseramento */}
              {membershipModalOpen && (
                <Dialog open={membershipModalOpen} onClose={() => setMembershipModalOpen(false)} maxWidth='xs' fullWidth>
                  <DialogTitle>Seleziona Tesseramento</DialogTitle>
                  <DialogContent>
                    <List>
                      {membershipList.map((m) => (
                        <ListItem
                          button
                          key={m._id}
                          onClick={() => {
                            setSelectedMembership(m);
                            setMembershipModalOpen(false);
                          }}
                        >
                          <ListItemText primary={m.name || m.title || 'Tesseramento'} secondary={m.description} />
                        </ListItem>
                      ))}
                    </List>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setMembershipModalOpen(false)}>Chiudi</Button>
                  </DialogActions>
                </Dialog>
              )}
              {/* Payment Type Selection for Add Mode */}
              {selectedMembership && (
                <FormControl fullWidth>
                  <InputLabel>Tipo di Pagamento</InputLabel>
                  <Select
                    name="paymentType"
                    value={form.paymentType}
                    onChange={handleChange}
                    label="Tipo di Pagamento"
                    disabled={loading}
                  >
                    <MenuItem value="cash">Contanti</MenuItem>
                    <MenuItem value="bank_transfer">Bonifico</MenuItem>
                  </Select>
                </FormControl>
              )}
            </>
          ) : (
            <>
              {/* <TextField
                name="userId"
                label="ID Utente"
                value={form.userId}
                onChange={handleChange}
                required
                fullWidth
                InputProps={{ readOnly: isView || isEdit }}
              /> */}

              {/* User Information Display */}
              {memberUser && (
                <Box sx={{ p: 2, mb: 2, borderColor: 'grey.300', borderWidth: 1, borderStyle: 'solid', borderRadius: 2, backgroundColor: 'grey.50' }}>
                  <Typography variant='h6' gutterBottom>
                    Informazioni Utente
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant='body2' color='text.secondary'>
                      <strong>ID</strong> {form.userId}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Typography variant='body2' color='text.secondary'>
                        <strong>Nome:</strong> {memberUser.name}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        <strong>Cognome:</strong> {memberUser.surname}
                      </Typography>
                    </Box>
                    <Typography variant='body2' color='text.secondary'>
                      <strong>Email:</strong> {memberUser.email}
                    </Typography>
                  </Box>
                </Box>
              )}

              {isEdit && (
                <FormControl fullWidth>
                  <InputLabel>Stato Membership</InputLabel>
                  <Select name='status' value={form.status} onChange={handleChange} label='Stato Membership'>
                    <MenuItem value='active'>Attiva</MenuItem>
                    {/* <MenuItem value="expired">Scaduta</MenuItem> */}
                    {/* <MenuItem value="cancelled">Cancellata</MenuItem> */}
                    <MenuItem value='pending'>In attesa</MenuItem>
                  </Select>
                </FormControl>
              )}
              {/* Payment Type Selection for Edit Mode */}
              {isEdit && (
                <FormControl fullWidth>
                  <InputLabel>Tipo di Pagamento</InputLabel>
                  <Select
                    name="paymentType"
                    value={form.paymentType}
                    onChange={handleChange}
                    label="Tipo di Pagamento"
                    disabled={loading}
                  >
                    <MenuItem value="cash">Contanti</MenuItem>
                    <MenuItem value="bank_transfer">Bonifico</MenuItem>
                  </Select>
                </FormControl>
              )}
            </>
          )}
          {error && <Box color='error.main'>{error}</Box>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Annulla
        </Button>
        {!isView && (
          <Button onClick={handleSubmit} variant='contained' disabled={loading || (isAdd && (!selectedUser || !selectedMembership))}>
            {isAdd ? 'Aggiungi' : 'Salva'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ModalMember;
